/**
 * The Bank Reconciliation module's route table (CLAUDE.md §8.6).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `BANKREC_ROUTES` in. Keeping them here means
 * the paths, the permissions and the components that implement them are decided
 * in one place by whoever owns the module.
 *
 * Two orderings are load-bearing and both are the same trick AR uses:
 * `/bankrec/sessions/new` and `/bankrec/imports/new` are declared **before**
 * their `:id(\d+)` siblings, so `new` can never be parsed as a document id. The
 * `\d+` constraint makes that safe anyway; the ordering makes it obvious.
 *
 * `BANKREC_PATHS` is the same set as strings, for links inside the module.
 * Links *out* of it — the journal behind a matched ledger line — are built and
 * then checked against the router by `linkIfRoutable` before being rendered: GL
 * owns that URL, and a guessed link that 404s in the middle of an auditor's
 * evidence chain is worse than plain text.
 */

import type { Router, RouteRecordRaw } from 'vue-router'

import { BANKREC_PERMISSIONS } from '@/modules/bankrec/permissions'

export const BANKREC_ROUTES: RouteRecordRaw[] = [
  {
    path: '/bankrec/sessions',
    name: 'bankrec-sessions',
    component: () => import('@/modules/bankrec/SessionsListView.vue'),
    meta: {
      titleKey: 'bankrec.routeTitles.sessions',
      permission: BANKREC_PERMISSIONS.sessionRead,
    },
  },
  {
    path: '/bankrec/sessions/:id(\\d+)',
    name: 'bankrec-session',
    component: () => import('@/modules/bankrec/ReconcileView.vue'),
    meta: {
      titleKey: 'bankrec.routeTitles.session',
      permission: BANKREC_PERMISSIONS.sessionRead,
    },
  },
  {
    path: '/bankrec/imports',
    name: 'bankrec-imports',
    component: () => import('@/modules/bankrec/ImportsView.vue'),
    meta: {
      titleKey: 'bankrec.routeTitles.imports',
      permission: BANKREC_PERMISSIONS.statementRead,
    },
  },
  {
    path: '/bankrec/imports/:id(\\d+)',
    name: 'bankrec-import',
    component: () => import('@/modules/bankrec/ImportView.vue'),
    meta: {
      titleKey: 'bankrec.routeTitles.import',
      permission: BANKREC_PERMISSIONS.statementRead,
    },
  },
  {
    path: '/bankrec/profiles',
    name: 'bankrec-profiles',
    component: () => import('@/modules/bankrec/ProfilesView.vue'),
    meta: {
      titleKey: 'bankrec.routeTitles.profiles',
      permission: BANKREC_PERMISSIONS.profileRead,
    },
  },
]

/**
 * C4's three additions to a reconciliation session, kept OUT of `BANKREC_ROUTES`
 * on purpose.
 *
 * `BANKREC_ROUTES` is already spread into `src/router/index.ts`, and
 * `src/nav/keys.test.ts` walks the live router asserting that every `titleKey`
 * resolves against the **merged** catalogue. These three carry `treasury.*` keys
 * that live in `web/src/modules/treasury/locales.*.json` and do not reach
 * `web/src/locales/en.json` until `scripts/merge-module-locales.py` runs — which
 * is the shared wiring packet's job, along with mounting these records. Spreading
 * them here would turn a green suite red for a reason that has nothing to do with
 * the screens.
 *
 * The wiring packet spreads `BANKREC_C4_ROUTES` beside `BANKREC_ROUTES` and runs
 * the merge; nothing else about them changes.
 */
export const BANKREC_C4_ROUTES: RouteRecordRaw[] = [
  {
    path: '/bankrec/sessions/:id(\\d+)/match-sets',
    name: 'bankrec-match-sets',
    component: () => import('@/modules/bankrec/MatchSetsView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.matchSets',
      permission: BANKREC_PERMISSIONS.sessionRead,
    },
  },
  {
    path: '/bankrec/sessions/:id(\\d+)/certificate',
    name: 'bankrec-certificate',
    component: () => import('@/modules/bankrec/CertifySessionView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.certificate',
      permission: BANKREC_PERMISSIONS.sessionRead,
    },
  },
  {
    path: '/bankrec/standing-rules',
    name: 'bankrec-standing-rules',
    component: () => import('@/modules/bankrec/StandingRulesView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.standingRules',
      permission: BANKREC_PERMISSIONS.profileRead,
    },
  },
]

/* ------------------------------------------------------------ own screens */

export const BANKREC_PATHS = {
  sessions: '/bankrec/sessions',
  session: (id: number) => `/bankrec/sessions/${id}`,
  imports: '/bankrec/imports',
  import: (id: number) => `/bankrec/imports/${id}`,
  profiles: '/bankrec/profiles',
  matchSets: (sessionId: number) => `/bankrec/sessions/${sessionId}/match-sets`,
  certificate: (sessionId: number) => `/bankrec/sessions/${sessionId}/certificate`,
  standingRules: '/bankrec/standing-rules',
} as const

/** The imports of one bank account — the hop from a session to its statements. */
export function importsForAccount(bankAccountId: number): string {
  return `${BANKREC_PATHS.imports}?bankAccountId=${bankAccountId}`
}

/* -------------------------------------------------------- links elsewhere */

/** A posted journal entry, from the ledger line a statement line matched. */
export function journalRoute(entryId: number): string {
  return `/gl/journal-entries/${entryId}`
}

/**
 * The path, but only if this app actually has a screen for it.
 *
 * The router's catch-all matches everything, so "resolves" is not the question —
 * "resolves to something other than the not-found page" is. A path the router
 * would send to the 404 screen comes back as null and the caller renders text.
 */
export function linkIfRoutable(router: Router, path: string | null): string | null {
  if (path === null || path === '') return null
  try {
    const resolved = router.resolve(path)
    if (resolved.matched.length === 0) return null
    if (resolved.matched.every((record) => record.name === 'not-found')) return null
    return path
  } catch {
    // An unparseable path is not a link either.
    return null
  }
}
